// src/utils/exportUtils.js
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

/**
 * Export JSON rows to Excel with optional summary sheet.
 */
export function exportToExcel({
  title,
  id,
  rows,
  headers,
  summaryRows = [],
  isFinancial = false,
  columnDisplayNames = {},
}) {
  if (!rows.length) {
    throw new Error("No data to export")
  }

  // Main sheet
  const data = rows.map(r => {
    const out = {}
    headers.forEach(h => {
      out[columnDisplayNames[h] || h] = r[h]
    })
    return out
  })
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, title)

  // Summary sheet
  if (isFinancial && summaryRows.length) {
    const sumSheet = XLSX.utils.aoa_to_sheet(summaryRows)
    XLSX.utils.book_append_sheet(wb, sumSheet, "Summary")
  }

  const filename = `${title}_${id}_${new Date().toISOString().split("T")[0]}.xlsx`
  XLSX.writeFile(wb, filename)
}

/**
 * Export JSON rows to PDF with optional summary table.
 */
export function exportToPDF({
  title,
  id,
  rows,
  headers,
  summaryRows = [],
  isFinancial = false,
  columnDisplayNames = {},
}) {
  if (!rows.length) {
    throw new Error("No data to export")
  }

  const doc = new jsPDF()
  // Header
  doc.setFontSize(18)
  doc.text(`${title} – ${id}`, 14, 22)
  doc.setFontSize(11)
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30)

  // Summary table
  if (isFinancial && summaryRows.length) {
    autoTable(doc, {
      startY: 45,
      head: [summaryRows[0]],
      body: summaryRows.slice(1),
      theme: "grid",
      headStyles: { fillColor: [66, 139, 202] },
    })
  }

  // Main table
  const startY = isFinancial ? 80 : 40
  const displayHeaders = headers.map(h => columnDisplayNames[h] || h)
  const body = rows.map(r => headers.map(h => {
    const v = r[h]
    if (v == null) return ""
    return typeof v === "number" ? v.toLocaleString() : String(v)
  }))

  autoTable(doc, {
    startY,
    head: [displayHeaders],
    body,
    theme: "grid",
    headStyles: { fillColor: [66, 139, 202] },
    didDrawPage: dataArg => {
      doc.setFontSize(10)
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        dataArg.settings.margin.left,
        doc.internal.pageSize.height - 10
      )
    },
  })

  const filename = `${title}_${id}_${new Date().toISOString().split("T")[0]}.pdf`
  doc.save(filename)
}

/**
 * Export JSON rows to CSV.
 */
export function exportToCSV({ title, id, rows, headers }) {
  if (!rows.length) {
    throw new Error("No data to export")
  }
  const csv = [
    headers.join(","),
    ...rows.map(r => headers.map(h => {
      const v = r[h] == null ? "" : String(r[h])
      return v.includes(",") ? `"${v}"` : v
    }).join(","))
  ].join("\n")

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  saveAs(blob, `${title}_${id}_${new Date().toISOString().split("T")[0]}.csv`)
}
